import { lazy, useEffect, useState } from "react";
import { Router, useLocation, useNavigate } from "@reach/router";
import { Tooltip } from "react-tooltip";
import 'react-tooltip/dist/react-tooltip.css';

import actions from "./actions";
import Suspense from "./Suspense/Suspense";
import useQuery from "../hooks/useQuery";
import useUser from "../hooks/useUser";
import useSocket from 'hooks/useSocket'
import useBetaFeatures from "hooks/useBetaFeatures";
import useDraggableSupportWidget from "hooks/useDraggableSupportWidget";
import useStoreRooms from "./Room/useStore";
import Navbar from "./Navbar/Navbar";
import Events from "./Events/Events";
import Consumers from "./Consumers/Consumers";
import AppDownloadBanner from "Components/AppDownloadBanner/AppDownloadBanner";
import NewUiBanner from "Components/NewUiBanner/NewUiBanner";
import { isWebRoute, mapToWebPath } from "lib/webRoutes";
import { captureUiPreference, readUiPreference } from "lib/uiPreference";
import { Form as SubscriptionForm } from "./Setup/Subscription/Form"
import { SubscriptionEntry } from "./Setup/Subscription/SubscriptionEntry"

const { REACT_APP_WEB_URL } = process.env
const WEB_URL = REACT_APP_WEB_URL || "https://web.qa.drapp.la"

const Notify = lazy(() => import("./Notify/Notify"));
const Room = lazy(() => import("./Room/Room"));
// Canal mobile de Escriba (§7.8 del plan): a diferencia de Events/Consumers
// (eager, uso constante de TODO usuario), esta ruta es de uso ocasional —
// mismo criterio que Odontograma/Percentiles dentro de la HCE. Sin lazy, el
// bundle de grabación/upload viajaría a CADA carga de drmanager sin que el
// flag lo autorice (hallazgo de la auditoría de deploy-apagado, 2026-08-24).
const EscribaMobile = lazy(() => import("./EHR/V2/Escriba/EscribaMobile"));
const Consumer = lazy(() => import("./Consumers/Modal/Modal"));
const Location = lazy(() => import("./Location/Modal"));
const MessagesForm = lazy(() => import("./Messages/Form/Form"));
const Onboarding = lazy(() => import("./Onboarding/Onboarding"));
const Setup = lazy(() => import("./Setup/Setup"));
const Locked = lazy(() => import("./Teams/Locked"));
const Prescription = lazy(() => import("./Prescription/Prescription"));
const Monitor = lazy(() => import("./Monitor/Monitor"));
const MigrateToTeam = lazy(() => import("./Consumers/MigrateToTeam/MigrateToTeam"));
const Planes = lazy(() => import("./Planes/Planes"));

const {
  REACT_APP_HB_TRACKING_CODE
} = process.env

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const teams = useQuery("teams", {
    initialData: [],
  });
  const user = useUser();
  const rooms = useStoreRooms();
  const [team, setTeam] = useState();
  const socket = useSocket()
  const HubSpotUserToken = useQuery('hs/user/auth')
  const features = useBetaFeatures()
  // El banner de T&C tiene prioridad sobre los otros dos banners bottom-fixed:
  // si está visible, se suprimen para que no se superpongan.
  // Bug real (Trello "V2 | Arreglos de Responsive Design", 2026-09-04): el
  // botón de soporte (widget de HubSpot, inyectado más abajo) tiene
  // position:fixed sin forma de reubicarlo — a veces tapa otros elementos.
  // Se arrastra el widget entero (overlay que cubre la burbuja) y un tap abre
  // el chat; ver hooks/useDraggableSupportWidget.js para el detalle de por qué
  // hace falta el overlay sobre el iframe cross-origin. La posición arrastrada
  // persiste (localStorage) y se mantiene entre secciones.
  useDraggableSupportWidget()

  useEffect(() => {
    actions.onChangeLocation();
  }, [location.pathname]);

  // `?ui=legacy` / `?ui=next` gana sobre el flag mientras dure la sesión de
  // la tab — herramienta de soporte/QA y anti-loop con web (ver
  // UI_REFACTOR_PLAN.md §7). Se captura ANTES de evaluar el redirect de
  // abajo, no reemplaza esa lógica.
  useEffect(() => {
    captureUiPreference(location.search);
  }, [location.search]);

  // Redirect a drapp-la/web — deliberadamente FUERA del guard de resolución
  // de team de arriba: ese efecto decide "a qué team en QUÉ ruta de
  // drmanager" (o manda a /teams/new), esto decide "esa ruta ya resuelta,
  // ¿la tiene que servir web en su lugar?". Mezclarlos haría que un bug acá
  // pudiera romper la resolución de team para TODOS los usuarios, migrados
  // o no — separarlos acota el blast radius a quien tiene el flag prendido.
  // No bloqueante: no toca `team`/`setTeam`, así que drmanager renderiza
  // exactamente igual que hoy mientras se decide si hace falta el salto.
  useEffect(() => {
    if (!team || features.isLoading) return;
    if (readUiPreference() === "legacy") return;
    if (!features.webUi) return;
    if (!isWebRoute(location.pathname)) return;

    const target = mapToWebPath(location.pathname);
    if (!target) return;

    window.location.replace(`${WEB_URL}${target}?ui=next`);
  }, [team, features.isLoading, features.webUi, location.pathname]);

  useEffect(() => {
    if (
      teams.isFetched &&
      // Bug real (auditoría 2026-08-16): un fallo transitorio de red acá
      // dejaba teams.data en el initialData ([]) con isFetched:true — se
      // veía IDÉNTICO a "el usuario no tiene equipos" y el efecto navegaba
      // a /teams/new como si el usuario recién se estuviera registrando,
      // arriesgando la creación de un equipo duplicado. Con isError no se
      // navega a ningún lado (react-query reintenta solo).
      !teams.isError &&
      user.query.isFetched &&
      user.state.email &&
      location.pathname !== "/teams/new"
    ) {
      const team =
        teams.data.find((team) => location.pathname.includes(team.id)) ||
        teams.data.find((team) => team.id === user.state.teamID) ||
        teams.data.find((team) => team.default) ||
        teams.data[0];
      let search = new URLSearchParams(location.search)
      // Guarda anti-open-redirect: `next` tiene que empezar con UNA sola
      // `/` — nunca con `//`, que el browser puede interpretar como una
      // URL protocol-relative a otro host. Ningún `next` legítimo de hoy
      // la viola: siempre nace de `window.location.pathname` (+ search
      // desde el fix de index.js), que nunca produce `//` al inicio.
      const nextParam = search.get('next')
      const nextIsSafe = nextParam && nextParam.startsWith('/') && !nextParam.startsWith('//')
      if (nextIsSafe) {
        navigate(nextParam, { replace: true });
      } else if (!team && teams.data.length === 0) {
        navigate("/teams/new", { replace: true });
      } else if (!location.pathname.includes(team.id) && !user.state.isStaff) {
        navigate(`/${team.id}/events/agenda`, { replace: true });
      } else if (!location.pathname.includes("/teams/") && user.state.isStaff) {
        navigate(`/${team.id}/events/agenda`, { replace: true });
      }

      setTeam(team);
    }
  }, [
    location.pathname,
    location.search,
    teams.data,
    teams.isFetched,
    user.query.isFetched,
    user.state.email,
    user.state.teamID,
    user.state.isStaff,
  ]);

  useEffect(() => {
    let email = user.state?.email

    if (email) {
      let _hsq = window._hsq = window._hsq || [];
      _hsq.push(['identify', { email }]);
    }
  }, [user.state.email]);

  useEffect(() => {
    const identificationEmail = user.state?.email
    const identificationToken = HubSpotUserToken.data?.token
    if (REACT_APP_HB_TRACKING_CODE && identificationToken && identificationEmail) {
      let _hsq = window._hsq = window._hsq || [];
      _hsq.push(['setPath', window.location.pathname]);

      const script = document.createElement('script');
      script.src = `//js.hs-scripts.com/${REACT_APP_HB_TRACKING_CODE}.js`;
      script.async = true;
      script.defer = true;
      script.id = 'hs-script-loader';

      window.hsConversationsSettings = {
        identificationEmail,
        identificationToken
      };

      document.body.appendChild(script);
    }
  }, [HubSpotUserToken.data, user.state.email, REACT_APP_HB_TRACKING_CODE])

  useEffect(() => {
    if (!user.team?.id || !user.state.email) return;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      user_properties: {
        user_role: user.team.type,
      },
    });
  }, [user.team?.id, user.state.email]);

  useEffect(() => {
    if (!sessionStorage.getItem('just_logged_in')) return;
    if (!user.team?.id || !user.state.email) return;

    sessionStorage.removeItem('just_logged_in');
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'sesion_iniciada',
      team_id: user.team.id,
      user_role: user.team.type,
    });
  }, [user.team?.id, user.state.email]);

  useEffect(() => {
    var _hsq = window._hsq = window._hsq || [];
    _hsq.push(['setPath', location.pathname]);
    _hsq.push(['trackPageView']);
    try {
      const status = window.HubSpotConversations.widget.status();

      if (status.loaded) {
        window.HubSpotConversations.widget.refresh();
      } else {
        window.HubSpotConversations.widget.load();
      }
    } catch {}
  }, [location.pathname])

  return !socket ? (
    <div className="vh-100 p-5 d-flex justify-content-center align-items-center">
      <div className="spinner-border" role="status"></div>
    </div>
  ) : location.pathname === "/teams/new" ? (
    <Suspense>
      <Onboarding />
    </Suspense>
  ) : !team ? (
    <div className="vh-100 p-5 d-flex justify-content-center align-items-center">
      <div className="spinner-border" role="status"></div>
    </div>
  ) : location.pathname.includes('/monitor') ? (
    <Suspense>
      <Monitor />
    </Suspense>
  ) : (
    <Suspense>
      <>
        {(!team.locked || user.state.isStaff) && (
          <Navbar />
        )}
        {team.locked && (
          <Locked />
        )}
        <Router>
          <Consumers path="/teams/:teamID/consumers/*" />
          {(!team.locked || user.state.isStaff) && (
            <>
              <Events path="/teams/:teamID/events/*" />
              <Setup path="/teams/:teamID/setup/*" />
              <Planes path="/teams/:teamID/planes" />
              <EscribaMobile path="/teams/:teamID/escriba/:recordingID" />
            </>
          )}
          {user.state.isStaff && (
            <MigrateToTeam path="/teams/:teamID/migrate-to-team" />
          )}
          
          {/* Card 1053: el formulario se entra con el plan (/suscripcion/pro). Sin plan es el
              alta de siempre hasta que se prendan los planes; con el enforcement va a Planes. */}
          <SubscriptionEntry path="/teams/:teamID/suscripcion" />
          <SubscriptionForm path="/teams/:teamID/suscripcion/:plan" />
          <SubscriptionEntry path="/teams/:teamID/subscription" />
          <SubscriptionForm path="/teams/:teamID/subscription/:plan" />
        </Router>
        <Notify />
        <Consumer />
        <MessagesForm />
        <Location />
        {/* Fuera del <Router> a propósito: cada montaje del banner cuenta
            como una aparición y sólo tiene dos. Adentro, cada navegación
            quemaría una y el banner moriría en el primer minuto de uso.
            Con la cuenta bloqueada no va: ofrecerle la app a alguien que no
            puede usar el panel es ruido, y gasta una de las dos apariciones. */}
        {!team.locked && <AppDownloadBanner />}
        {!team.locked && <NewUiBanner teamId={team.id} />}
        <Tooltip id="app-tooltip" />
        <Tooltip id="internal-note-tooltip" className="internal-note-tooltip" variant="light" noArrow />
        {rooms.ids.map((id) => (
          <Room key={`room-${id}`} name={id} onClose={() => rooms.close(id)} />
        ))}
        <Prescription />
      </>
    </Suspense>
  );
}

export default App;
